import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
  Router,
  ActivatedRoute
} from '@angular/router';
import { KeycloakAuthGuard, KeycloakService } from 'keycloak-angular';
import rolemap from './../../rolemap.json';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent
} from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';


// ------------------ AuthGuard ------------------
@Injectable({
  providedIn: 'root',
})
export class AuthGuard extends KeycloakAuthGuard {
  private enabledRoutes: string[] = [];

  constructor(
    protected override readonly router: Router,
    protected readonly keycloak: KeycloakService,
    protected path: ActivatedRoute
  ) {
    super(router, keycloak);
  }

  async isAccessAllowed(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    if (!this.authenticated) {
      await this.keycloak.login({ redirectUri: window.location.origin + state.url });
    }

    const userRoles = this.keycloak.getUserRoles();
    this.enabledRoutes = [];
    let omniAccess = false;

    rolemap.forEach((roleItem: any) => {
      Object.entries(roleItem).forEach(([roleKey, paths]) => {
        if (userRoles.includes(roleKey) && Array.isArray(paths)) {
          this.enabledRoutes.push(...paths);
        }
        if (roleKey.toLowerCase().includes('omni')) {
          omniAccess = true;
        }
      });
    });

    if (omniAccess) {
      this.enabledRoutes.push('/omni/dashboard');
    }

    const ignoredRoles = [
      "offline_access",
      "allow_access",
      "default-roles-srt_dev",
      "manage-account",
      "manage-account-links",
      "view-profile",
      "uma_authorization",
      "default-roles-srt_prod",
      "default-roles-srt_stg",
      "default-roles-srt_uat",
    ];
    const validRoles =
      this.keycloak.getKeycloakInstance()?.realmAccess?.roles?.filter(
        (role: string) => !ignoredRoles.includes(role)
      ) || [];
    if (validRoles.length === 0) {
      this.router.navigate(['/request-access']);
      return false;
    }

    // const cleanedUrl = state.url.split('?')[0];
    const cleanedUrl = state.url.split('?')[0].split('#')[0]; //401 error 
    if (!this.enabledRoutes.includes(cleanedUrl) && cleanedUrl !== '/') {
      this.router.navigate(['/unauthorised']);
      return false;
    }

    return this.authenticated;
  }

  getAccessToken() {
    return this.keycloak.getKeycloakInstance().token;
  }
}


// ------------------ AuthInterceptor ------------------
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(private keycloak: KeycloakService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return from(this.keycloak.getToken()).pipe(
      switchMap((bearerToken) => {
        if (!req.headers.has('Authorization')) {
          req = req.clone({
            headers: req.headers.set('Authorization', `Bearer ${bearerToken}`),
          });
        }
        return next.handle(req);
      })
    );
  }
}
